import is from '@sindresorhus/is';

import type {
    CareerFrameworkViewId,
    FrameworkModeId,
    JobArchitectureSidebarId,
    JobProfileViewId,
    PersonalPreferencesSidebarId,
    PositionArchitectureSidebarId,
    SidebarId,
    SubscriptionBillingSidebarId,
} from './constants';
import {
    isJobArchitectureSidebarId,
    isPersonalPreferencesSidebarId,
    isPositionArchitectureSidebarId,
    isSubscriptionBillingSidebarId,
} from './constants';

export const companySettingsRoute = {
    params: {
        jobArchitectureId: 'jobArchitectureId',
        jobProfileView: 'jobProfileView',
    },
    patterns: {
        entry: '/companySettings',
        jobArchitecture: '/companySettings/job-architecture/:jobArchitectureId?',
        organization: '/companySettings/organization',
        personalPreferences: '/companySettings/personal-preferences',
        positionArchitecture: '/companySettings/position-architecture',
        subscriptionBilling: '/companySettings/subscription-billing',
    },
    searchParams: {
        careerFrameworkView: 'careerFrameworkView',
        frameworkId: 'frameworkId',
        frameworkMode: 'frameworkMode',
        from: 'from',
        jobProfileView: 'jobProfileView',
        openCareerFrameworkEdit: 'openCareerFrameworkEdit',
        openInvite: 'openInvite',
        range: 'range',
        returnUrl: 'returnUrl',
        sidebar: 'sidebar',
        sort: 'sort',
        to: 'to',
    },
};

export type JobArchitectureSettingsOptions = {
    readonly careerFrameworkView?: CareerFrameworkViewId;
    readonly frameworkId?: string;
    readonly frameworkMode?: FrameworkModeId;
    readonly jobProfileView?: JobProfileViewId;
    readonly openCareerFrameworkEdit?: boolean;
    readonly sidebar?: JobArchitectureSidebarId;
};

export const usersAndRolesRoute = {
    patterns: {
        usersAndRoles: '/company/users',
    },
};

export const knowledgeBaseRoute = {
    patterns: {
        knowledgeBase: '/company/knowledge-base',
    },
};

export type OrganizationSettingsUrlOptions = {
    readonly openInvite?: boolean;
    readonly sidebar?: string;
};

export const buildOrganizationSettingsUrl = (
    sidebarItem?: string,
    options?: OrganizationSettingsUrlOptions,
): string => {
    const params = new URLSearchParams();

    const sidebar = options?.sidebar ?? sidebarItem;
    if (!is.undefined(sidebar)) {
        params.set(companySettingsRoute.searchParams.sidebar, sidebar);
    }

    if (options?.openInvite === true) {
        params.set(companySettingsRoute.searchParams.openInvite, '1');
    }

    const queryString = params.toString();

    return is.emptyString(queryString)
        ? companySettingsRoute.patterns.organization
        : `${companySettingsRoute.patterns.organization}?${queryString}`;
};

export const buildJobArchitectureSettingsUrl = (
    jobArchitectureId?: string,
    options?: JobArchitectureSettingsOptions,
): string => {
    const basePattern = companySettingsRoute.patterns.jobArchitecture;
    const baseUrl = is.undefined(jobArchitectureId)
        ? basePattern.replace('/:jobArchitectureId?', '')
        : basePattern.replace(':jobArchitectureId?', jobArchitectureId);

    const url = is.undefined(options?.jobProfileView) ? baseUrl : `${baseUrl}/job-profile/${options.jobProfileView}`;

    const params = new URLSearchParams();

    if (!is.undefined(options?.sidebar)) {
        params.set(companySettingsRoute.searchParams.sidebar, options.sidebar);
    }

    if (!is.undefined(options?.careerFrameworkView)) {
        params.set(companySettingsRoute.searchParams.careerFrameworkView, options.careerFrameworkView);
    }

    if (!is.undefined(options?.frameworkId)) {
        params.set(companySettingsRoute.searchParams.frameworkId, options.frameworkId);
    }

    if (!is.undefined(options?.frameworkMode)) {
        params.set(companySettingsRoute.searchParams.frameworkMode, options.frameworkMode);
    }

    if (options?.openCareerFrameworkEdit === true) {
        params.set(companySettingsRoute.searchParams.openCareerFrameworkEdit, 'true');
    }

    const queryString = params.toString();

    return is.emptyString(queryString) ? url : `${url}?${queryString}`;
};

const withSidebar = (pathname: string, sidebarItem: SidebarId | undefined): string =>
    is.undefined(sidebarItem)
        ? pathname
        : `${pathname}?${new URLSearchParams({ [companySettingsRoute.searchParams.sidebar]: sidebarItem }).toString()}`;

export const buildPersonalPreferencesSettingsUrl = (sidebarItem?: PersonalPreferencesSidebarId): string =>
    withSidebar(companySettingsRoute.patterns.personalPreferences, sidebarItem);

export const buildSubscriptionBillingSettingsUrl = (sidebarItem?: SubscriptionBillingSidebarId): string =>
    withSidebar(companySettingsRoute.patterns.subscriptionBilling, sidebarItem);

export const buildPositionArchitectureSettingsUrl = (sidebarItem?: PositionArchitectureSidebarId): string =>
    withSidebar(companySettingsRoute.patterns.positionArchitecture, sidebarItem);

export const buildSettingsSidebarUrl = (sidebarId: SidebarId): string => {
    if (isJobArchitectureSidebarId(sidebarId)) {
        return buildJobArchitectureSettingsUrl(undefined, { sidebar: sidebarId });
    }

    if (isPersonalPreferencesSidebarId(sidebarId)) {
        return buildPersonalPreferencesSettingsUrl(sidebarId);
    }

    if (isSubscriptionBillingSidebarId(sidebarId)) {
        return buildSubscriptionBillingSettingsUrl(sidebarId);
    }

    if (isPositionArchitectureSidebarId(sidebarId)) {
        return buildPositionArchitectureSettingsUrl(sidebarId);
    }

    return buildOrganizationSettingsUrl(sidebarId);
};

/** The standalone career frameworks page, which replaces the settings one when it is enabled. */
export const careerFrameworksPagePath = '/career-frameworks';

export const careerFrameworksPageSearchParams = {
    architecture: 'architecture',
    framework: 'framework',
    mode: 'mode',
} as const;

/** Opens the career frameworks page on its template picker instead of the framework list. */
export const careerFrameworksPageTemplatesUrl = `${careerFrameworksPagePath}?${careerFrameworksPageSearchParams.mode}=add`;

/** The guided flow for creating a career framework. */
export const careerFrameworksNewPagePath = `${careerFrameworksPagePath}/new`;

/** Where the career frameworks page lands when the job architecture has no framework yet. */
export const careerFrameworksGetStartedPagePath = `${careerFrameworksPagePath}/get-started`;

export const buildCareerFrameworksUrl = (path: string, jobArchitectureId: string): string =>
    `${path}?${new URLSearchParams({ [careerFrameworksPageSearchParams.architecture]: jobArchitectureId }).toString()}`;
